import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  ReactNode,
} from 'react'
import {
  computeStats,
  removeLineDuplicates,
  removeWordDuplicates,
  removeLineAndWordDuplicates,
  applyTextOps,
  type TextStats,
} from '../lib/deduplication'

interface AppState {
  inputText: string
  outputText: string
  inputStats: TextStats
  outputStats: TextStats
  caseSensitive: boolean
  removeEmptyLines: boolean
  trimWhitespace: boolean
  sortOutput: boolean
  removedLines: number
  removedWords: number
  setInputText: (text: string) => void
  clearInput: () => void
  toggleCaseSensitive: () => void
  toggleRemoveEmptyLines: () => void
  toggleTrimWhitespace: () => void
  toggleSortOutput: () => void
  applyRemoveLines: () => void
  applyRemoveWords: () => void
  applyRemoveHybrid: () => void
}

const emptyStats: TextStats = { lines: 0, words: 0, chars: 0, charsNoSpace: 0 }

const AppContext = createContext<AppState | null>(null)

export function AppProvider({ children }: { children: ReactNode }) {
  const [inputText, setInputTextRaw] = useState('')
  const [outputText, setOutputText] = useState('')
  const [inputStats, setInputStats] = useState<TextStats>(emptyStats)
  const [outputStats, setOutputStats] = useState<TextStats>(emptyStats)
  const [caseSensitive, setCaseSensitive] = useState(true)
  const [removeEmptyLines, setRemoveEmptyLines] = useState(false)
  const [trimWhitespace, setTrimWhitespace] = useState(false)
  const [sortOutput, setSortOutput] = useState(false)
  const [removedLines, setRemovedLines] = useState(0)
  const [removedWords, setRemovedWords] = useState(0)
  const [lastFn, setLastFn] = useState<'lines' | 'words' | 'hybrid' | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const runDedup = useCallback(
    (
      mode: 'lines' | 'words' | 'hybrid',
      text: string,
      cs: boolean,
      opts: { removeEmpty: boolean; trim: boolean; sort: boolean }
    ) => {
      let result
      if (mode === 'lines') result = removeLineDuplicates(text, cs)
      else if (mode === 'words') result = removeWordDuplicates(text, cs)
      else result = removeLineAndWordDuplicates(text, cs)
      const finalOutput = applyTextOps(result.output, opts)
      setOutputText(finalOutput)
      setOutputStats(computeStats(finalOutput))
      setRemovedLines(result.removedLines)
      setRemovedWords(result.removedWords)
    },
    []
  )

  const setInputText = useCallback(
    (text: string) => {
      setInputTextRaw(text)
      setInputStats(computeStats(text))
      if (lastFn) {
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => {
          runDedup(lastFn, text, caseSensitive, {
            removeEmpty: removeEmptyLines,
            trim: trimWhitespace,
            sort: sortOutput,
          })
        }, 300)
      }
    },
    [lastFn, caseSensitive, removeEmptyLines, trimWhitespace, sortOutput, runDedup]
  )

  const clearInput = useCallback(() => {
    setInputTextRaw('')
    setInputStats(emptyStats)
    setOutputText('')
    setOutputStats(emptyStats)
    setRemovedLines(0)
    setRemovedWords(0)
    setLastFn(null)
  }, [])

  const toggleCaseSensitive = useCallback(() => {
    const next = !caseSensitive
    setCaseSensitive(next)
    if (lastFn) runDedup(lastFn, inputText, next, { removeEmpty: removeEmptyLines, trim: trimWhitespace, sort: sortOutput })
  }, [caseSensitive, lastFn, inputText, removeEmptyLines, trimWhitespace, sortOutput, runDedup])

  const toggleRemoveEmptyLines = useCallback(() => {
    const next = !removeEmptyLines
    setRemoveEmptyLines(next)
    if (lastFn) runDedup(lastFn, inputText, caseSensitive, { removeEmpty: next, trim: trimWhitespace, sort: sortOutput })
  }, [removeEmptyLines, lastFn, inputText, caseSensitive, trimWhitespace, sortOutput, runDedup])

  const toggleTrimWhitespace = useCallback(() => {
    const next = !trimWhitespace
    setTrimWhitespace(next)
    if (lastFn) runDedup(lastFn, inputText, caseSensitive, { removeEmpty: removeEmptyLines, trim: next, sort: sortOutput })
  }, [trimWhitespace, lastFn, inputText, caseSensitive, removeEmptyLines, sortOutput, runDedup])

  const toggleSortOutput = useCallback(() => {
    const next = !sortOutput
    setSortOutput(next)
    if (lastFn) runDedup(lastFn, inputText, caseSensitive, { removeEmpty: removeEmptyLines, trim: trimWhitespace, sort: next })
  }, [sortOutput, lastFn, inputText, caseSensitive, removeEmptyLines, trimWhitespace, runDedup])

  const applyRemoveLines = useCallback(() => {
    setLastFn('lines')
    runDedup('lines', inputText, caseSensitive, { removeEmpty: removeEmptyLines, trim: trimWhitespace, sort: sortOutput })
  }, [inputText, caseSensitive, removeEmptyLines, trimWhitespace, sortOutput, runDedup])

  const applyRemoveWords = useCallback(() => {
    setLastFn('words')
    runDedup('words', inputText, caseSensitive, { removeEmpty: removeEmptyLines, trim: trimWhitespace, sort: sortOutput })
  }, [inputText, caseSensitive, removeEmptyLines, trimWhitespace, sortOutput, runDedup])

  const applyRemoveHybrid = useCallback(() => {
    setLastFn('hybrid')
    runDedup('hybrid', inputText, caseSensitive, { removeEmpty: removeEmptyLines, trim: trimWhitespace, sort: sortOutput })
  }, [inputText, caseSensitive, removeEmptyLines, trimWhitespace, sortOutput, runDedup])

  return (
    <AppContext.Provider
      value={{
        inputText,
        outputText,
        inputStats,
        outputStats,
        caseSensitive,
        removeEmptyLines,
        trimWhitespace,
        sortOutput,
        removedLines,
        removedWords,
        setInputText,
        clearInput,
        toggleCaseSensitive,
        toggleRemoveEmptyLines,
        toggleTrimWhitespace,
        toggleSortOutput,
        applyRemoveLines,
        applyRemoveWords,
        applyRemoveHybrid,
      }}
    >
      {children}
    </AppContext.Provider>
  )
}

export function useApp(): AppState {
  const ctx = useContext(AppContext)
  if (!ctx) throw new Error('useApp must be used inside AppProvider')
  return ctx
}
